import { lsConstants } from "@/constants.ts";
import { beforeNow, unixSeconds } from "@/service/date.ts";
import { USER_SERVICE_URL } from "./index.ts";
import { getWorkspace } from "@/context/index.ts";

const { VITE_STAGE, VITE_AUTH_LOGOUT_REDIRECT } = import.meta.env;

const isProd = VITE_STAGE === "api";

type ApiResponse = { data?: unknown; error?: string };

/**
 * Makes an HTTP request with `fetch`
 * but throws an HttpError if the response is not ok
 * Overrides return type from default `any` so we have to handle it
 */
const request = async (req: Request) => {
  req.headers.append("Correlation-Id", crypto.randomUUID());
  const res = await fetch(req);
  const body = (await res.json()) as ApiResponse;
  if (!res.ok) {
    if (body.error === "Unauthorised Access") {
      throw new ExpiredError("Session expired");
    }

    throw new HttpError(body.error || `Error calling ${req.url}`, res.status);
  }
  return body;
};

type Path = `/${string}`;
type Options = RequestInit & {
  /** Request body, will be JSON.stringified */
  json?: object;
  /** Middleware to run before the request. Can mutate the request */
  before?: ((req: Request) => void)[];
};

/**
 * Creates an HTTP client with methods for get/post/pu/patch/delete
 * All requests will respect the baseUrl set upon client creation,
 * will always include credentials, and will always send JSON.
 * By default they will check the refresh token too.
 * @example
 * const client = new Client("http://example.com", { headers: { "X": "123" }});
 * await client.get("/thing");
 * await client.delete("/thing/1", { headers: "organisationId": "123" });
 */
export class Client {
  base: string;
  defaults: Options;
  constructor(base: string, defaults: Options = {}) {
    this.base = base;
    this.defaults = defaults;
  }
  async send(method: string, path: Path, options: Options = {}) {
    const before = options.before || this.defaults.before || [];
    const req = new Request(this.base + path, {
      ...this.defaults,
      method,
      headers: this.headers(options.headers),
      body: options.json ? JSON.stringify(options.json) : options.body,
    });
    for (const fn of before) await fn(req);
    return request(req);
  }
  /** Merge headers with defaults */
  headers(init?: HeadersInit) {
    const originalHeaders = new Headers(this.defaults.headers || {});
    const newHeaders = new Headers(init);
    for (const [key, value] of newHeaders.entries()) {
      originalHeaders.set(key, value);
    }
    return originalHeaders;
  }
  get = (path: Path, opts?: Options) => this.send("get", path, opts);
  post = (path: Path, opts?: Options) => this.send("post", path, opts);
  put = (path: Path, opts?: Options) => this.send("put", path, opts);
  patch = (path: Path, opts?: Options) => this.send("patch", path, opts);
  delete = (path: Path, opts?: Options) => this.send("delete", path, opts);
}

class HttpError extends Error {
  status: number;
  constructor(message: string, status: number) {
    super(message);
    this.status = status;
  }
}

interface RefreshOpts {
  redirectOnError?: boolean;
}

export const refreshToken = (options?: RefreshOpts) => async () => {
  const { redirectOnError = true } = options || {};

  // 1a.  Get expiration date from local storage
  const expDate = localStorage.getItem(lsConstants.TOKEN_EXPIRES);

  // 1b. User hasn't logged in yet so redirect to login
  if (!expDate) {
    if (redirectOnError) {
      window.location.href = authUrl(
        "/login",
        window.location.pathname + window.location.search
      );
      throw new AuthError("Please log in");
    }
    return;
  }

  // 1c.  Is cookie almost expired?
  if (almostExpired(expDate)) {
    // 1d.  If yes, then refetch token first then make the api call
    const response = await fetch(USER_SERVICE_URL + "/session", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      credentials: "include",
    });

    if (!response.ok) {
      // Token isn't valid anymore
      // This will render the RootBoundary and ask user to login
      throw new ExpiredError("Session expired");
    }

    const { expiresIn } = await response.json();
    if (expiresIn) {
      const expirationDate = unixSeconds() + expiresIn;
      localStorage.setItem(lsConstants.TOKEN_EXPIRES, expirationDate);
    }
  }
};

export const almostExpired = (expSeconds: string) => {
  const expMs = Number(expSeconds) * 1000;
  const instant = Temporal.Instant.fromEpochMilliseconds(expMs);
  return beforeNow(instant.subtract({ minutes: 5 }));
};

export const setOrgHeader = (req: Request) => {
  const { organisation } = getWorkspace();
  req.headers.set("Organisation-Id", organisation.id);
};

export const setWorkspaceHeader = (req: Request) => {
  const { workspace } = getWorkspace();
  req.headers.set("Workspace-Id", workspace.id);
};

export class AuthError extends Error {}
export class ExpiredError extends Error {}

const LOGIN_URL = `${USER_SERVICE_URL}/login`;
const LOGOUT_URL = isProd
  ? `https://auth.supply25.com/oauth/logout`
  : `https://auth-uat.supply25.com/oauth/logout`;

export const authUrl = (path: "/login" | "/logout", state?: string | null) => {
  const url = new URL(path === "/login" ? LOGIN_URL : LOGOUT_URL);

  if (state) url.searchParams.set("redirect", state);

  if (path === "/logout") {
    url.searchParams.set("post_logout_redirect_uri", VITE_AUTH_LOGOUT_REDIRECT);
  }

  return url.toString();
};
